import { TClientBody } from 'core/models/clientLBS'
import DefaultModal from '../defaultModal/defaultModal'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
    capitalizeFirstLetter,
    formatCurrencyBR,
    formatDateBr,
    formatDocument,
    formatPhoneNumber,
    formatRG,
    formatarCEP,
    formatBigName,
    returnSexoById,
    formatDateToSearch
} from 'core/utils/globalFunctions'
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ClientLbsService } from 'core/api/client/clientService'
import { useEffect, useMemo, useState } from 'react'
import { Order } from 'core/models/table'
import DataTablePagination from 'app/components/table/pagination/pagination'
import {
    InfoKey,
    InfoRowContainer,
    InfoTitle,
    InfoValue,
    Section,
    SectionBloc,
    SectionCol
} from 'core/theme/globalStyles'
import Spinner from 'app/components/spinner/spinner'
import theme from 'core/theme/theme'
import dayjs, { Dayjs } from 'dayjs'
import DateRangeDisplay from 'app/components/dateRange/dateRangeDisplay/dateRangeDisplay'
import styled from 'styled-components'

interface IClientDetailsProps {
    isOpen: boolean
    onClose: () => void
    onOpen: () => void
    client: TClientBody
}

const ClientDetailsModal = (props: IClientDetailsProps) => {
    const { isOpen, onClose, onOpen, client } = props

    const [tempSaleid, setTempSaleid] = useState<number | undefined>()
    const [showSalesItems, setShowSalesItems] = useState(false)

    const [rowsPerPage, setRowsPerPage] = useState(1000)
    const [page, setPage] = useState(0)
    const [count, setCount] = useState(0)
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState('name')

    const [itemsRowsPerPage, setItemsRowsPerPage] = useState(10)
    const [itemsPage, setItemsPage] = useState(0)
    const [itemsCount, setItemsCount] = useState(0)


    // Estado do intervalo de data
    const [initDate, setInitDate] = useState<Dayjs | null | undefined>(
        dayjs().startOf('year')
    )
    const [finalDate, setFinalDate] = useState<Dayjs | null | undefined>(
        dayjs().endOf('year')
    )

    // ? requisicao para as compras do cliente
    const clientSales = useQuery({
        queryKey: ['clientSales', client.id, page, rowsPerPage, finalDate, initDate],
        queryFn: () =>
            ClientLbsService.getClientSales(
                client.id,
                page,
                rowsPerPage,
                client.cpforcnpj,
                formatDateToSearch(initDate!),
                formatDateToSearch(finalDate!)

            ),
        staleTime: Infinity
    })

    // ? requisição para os itens de cada compra do cliente, condicional à existência de tempSaleid
    const clientSalesItems = useQuery({
        queryKey: ['clientSalesItems', tempSaleid, itemsPage, itemsRowsPerPage],
        queryFn: () =>
            tempSaleid
                ? ClientLbsService.getClientSalesItems(
                    tempSaleid,
                    itemsPage,
                    itemsRowsPerPage
                )
                : Promise.resolve(null),
        staleTime: Infinity,
        enabled: !!tempSaleid
    })

    // ? bloco a ser montado
    const clientPersonalInfos: IInfoProps[] = [
        { key: 'Nome', value: client?.name, formater: formatBigName },
        { key: 'RG', value: client?.rg, formater: formatRG },
        {
            key: 'CPF / CNPJ',
            value: client?.cpforcnpj,
            formater: formatDocument
        },
        {
            key: 'Data de nascimento',
            value: client?.birthdate,
            formater: formatDateBr
        },
        { key: 'Criado em', value: client?.createdAt, formater: formatDateBr },
        {
            key: 'Sexo',
            value: returnSexoById(client?.sexoId),
            formater: capitalizeFirstLetter
        },
        {
            key: 'Estado civil',
            value: client?.maritalStatus,
            formater: capitalizeFirstLetter
        },
        {
            key: 'Ativo',
            value: client?.active ? (
                <Box
                    sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: theme.COLORS.GREEN4,
                        marginTop: '4px'
                    }}
                ></Box>
            ) : (
                <Box
                    sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: theme.COLORS.RED,
                        marginTop: '4px'
                    }}
                ></Box>
            )
        }
    ]

    const clientAddressInfos: IInfoProps[] = [
        { key: 'CEP', value: client?.address?.zipCode, formater: formatarCEP },
        { key: 'Estado', value: client?.address?.uf },
        { key: 'Cidade', value: client?.address?.city },
        { key: 'Bairro', value: client?.address?.neighborhood },
        { key: 'Rua', value: client?.address?.road },
        { key: 'Número', value: client?.address?.number }
    ]

    const clientContactsInfos: IInfoProps[] = [
        {
            key: 'Celular principal',
            value: client?.contacts?.cellPhone,
            formater: formatPhoneNumber
        },
        {
            key: 'Celular secundário',
            value: client?.contacts?.cellPhone2,
            formater: formatPhoneNumber
        },
        {
            key: 'Telefone',
            value: client?.contacts?.telephone,
            formater: formatPhoneNumber
        },
        { key: 'Email', value: client?.contacts?.email }
    ]

    useEffect(() => {
        if (clientSales.isSuccess && clientSales.data) {
            setCount(clientSales.data.totalElements)
        }
        if (clientSalesItems.isSuccess && clientSalesItems.data) {
            setItemsCount(clientSalesItems.data.totalElements)
        }
    }, [
        clientSales.isSuccess,
        clientSales.data,
        clientSalesItems.isSuccess,
        clientSalesItems.data
    ])

    const totalValue = useMemo(
        () =>
            clientSales.data?.content.reduce(
                (acc, item) =>
                    acc + item.amount,
                0
            ),
        [clientSales.data]
    )

    return (
        <>
            <DefaultModal
                title="Detalhes do cliente"
                isOpen={isOpen}
                onClose={onClose}
                onOpen={onOpen}
                children={
                    <SectionCol>
                        <InfosComponent
                            items={[
                                {
                                    title: 'Informações gerais',
                                    infos: clientPersonalInfos
                                },
                                {
                                    title: 'Endereço',
                                    infos: clientAddressInfos
                                },
                                {
                                    title: 'Contatos',
                                    infos: clientContactsInfos
                                }
                            ]}
                        />
                        {clientSales.isLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 300,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : clientSales.data?.content && (
                            <TableContainer sx={{ maxHeight: 300, position: 'relative' }}>
                                {/* <StyledBox>
                                    <StyledTypography>Período: </StyledTypography>
                                    <DateRangeDisplay
                                        typeOfDatePicker={'month'}
                                        initialDate={initDate}
                                        endDate={finalDate}
                                        setInitialDate={setInitDate}
                                        setFinalDate={setFinalDate}
                                    />
                                </StyledBox> */}
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell >Código</TableCell>
                                            <TableCell>Vendedor</TableCell>
                                            <TableCell>Valor</TableCell>
                                            <TableCell align="left">Pagamento</TableCell>
                                            <TableCell align="left">Status</TableCell>
                                            <TableCell align="left">Tipo</TableCell>
                                            <TableCell align="left">Data</TableCell>
                                            <TableCell align="right">Itens</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clientSales.data?.content.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center">
                                                    Nenhuma venda encontrada
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            clientSales.data?.content.map((row) => (
                                                <TableRow key={row.saleId}>
                                                    <TableCell >{row.saleCod}</TableCell>
                                                    <TableCell component="th" scope="row">
                                                        {row.nameCollaborator}
                                                    </TableCell>
                                                    <TableCell >
                                                        {formatCurrencyBR(row.amount)}
                                                    </TableCell>
                                                    <TableCell >
                                                        {row.paymentMethod}
                                                    </TableCell>
                                                    <TableCell >{row.status}</TableCell>
                                                    <TableCell >{row.type}</TableCell>
                                                    <TableCell >
                                                        {formatDateBr(row.data)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            onClick={() => {
                                                                setShowSalesItems(true);
                                                                setTempSaleid(row.saleId);
                                                            }}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Divider />
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", padding: "1rem 1rem 1rem 1rem", color: theme.COLORS.YELLOW2 }}>
                            <Typography sx={{ fontWeight: "bold", }}>Total: {formatCurrencyBR(totalValue)}</Typography>
                        </Box>
                    </SectionCol>
                }
            />
            <DefaultModal
                title="Items da venda"
                isOpen={showSalesItems}
                onClose={() => setShowSalesItems(false)}
                onOpen={() => setShowSalesItems(true)}
            >
                <TableContainer
                    sx={{
                        position: 'relative',
                        maxHeight: 350
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell align="left">Nome</TableCell>
                                <TableCell align="left">Descrição</TableCell>
                                <TableCell align="right">Qtd</TableCell>
                                <TableCell align="right">Valor Unit.</TableCell>
                                <TableCell align="right">Valor total</TableCell>
                            </TableRow>
                        </TableHead>
                        {clientSalesItems.isLoading ? (
                            <Spinner
                                state={clientSales.isLoading}
                                size={10}
                                css={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%'
                                }}
                            />
                        ) : (
                            <TableBody>
                                {clientSalesItems.data?.content.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            Nenhum item encontrado
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    clientSalesItems.data?.content.map(
                                        (row) => (
                                            <TableRow key={row.productId}>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                >
                                                    {row.productId}
                                                </TableCell>
                                                <TableCell
                                                    component="th"
                                                    scope="row"
                                                >
                                                    {row.productName}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        row.characteristicsDescription
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row.itemSalesAmount}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBR(
                                                        row.unitaryValue
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrencyBR(
                                                        row.itemSalesAmount *
                                                        row.unitaryValue
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                )}
                            </TableBody>
                        )}
                    </Table>
                    <DataTablePagination
                        setPage={setItemsPage}
                        page={itemsPage}
                        setRowsPerPage={setItemsRowsPerPage}
                        rowsPerPage={itemsRowsPerPage}
                        count={itemsCount}
                    />
                </TableContainer>
            </DefaultModal>
        </>
    )
}

interface IInfosComponentProps {
    items: IBlockOfInfosProps[]
    style?: React.CSSProperties | undefined
}

export const InfosComponent = (props: IInfosComponentProps) => {
    const { items, style } = props

    return (
        <Section style={style}>
            {items.map((infos) => (
                <SectionBloc>
                    <BlockOfInfos
                        key={infos.title}
                        title={infos.title}
                        infos={infos.infos}
                    />
                </SectionBloc>
            ))}
        </Section>
    )
}

export interface IInfoProps {
    key: string
    value: any
    formater?: (value: any) => any
}

interface IBlockOfInfosProps {
    title: string
    infos: IInfoProps[]
}

// ? bloco de informações

const BlockOfInfos = (props: IBlockOfInfosProps) => {
    const { title, infos } = props

    return (
        <SectionCol>
            <div
                style={{
                    backgroundColor: theme.COLORS.YELLOW2,
                    padding: '0.5rem'
                }}
            >
                <InfoTitle>{title}</InfoTitle>
            </div>
            <div
                style={{
                    padding: '0.5rem'
                }}
            >
                {infos.map((info) => (
                    <InfoRow
                        key={info.key}
                        keyValue={info.key}
                        value={info.value}
                        formater={info.formater}
                    />
                ))}
            </div>
        </SectionCol>
    )
}

// ? linha de informação

interface IInfoRowProps {
    keyValue: string
    value: any
    formater?: (value: any) => any
}

const InfoRow = (props: IInfoRowProps) => {
    const { keyValue, value, formater } = props

    return (
        <InfoRowContainer>
            <InfoKey>{keyValue}</InfoKey>
            <Tooltip
                title={
                    keyValue === 'Email' ? value : keyValue === 'Nome' ? value : ''
                }
            >
                <InfoValue>{formater ? formater(value) : value}</InfoValue>
            </Tooltip>
        </InfoRowContainer>
    )
}

export default ClientDetailsModal


const StyledBox = styled(Box)`
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    width: 31% !important;
    padding-left: 1rem !important;
`

const StyledTypography = styled.div`
    color: ${theme.COLORS.YELLOW2} !important;
    font-weight: bold !important;
    font-size: 15px !important;
    text-align: center !important;
`